/**
 * MultiScatterLut — Hillaire 2020 multiple-scattering approximation LUT.
 *
 * Single-scattering alone makes the sky look too dark and "thin", especially
 * near the horizon and in dense (Mie-heavy) atmospheres. Hillaire §5
 * introduces a cheap, energy-conserving 2nd-order term that approximates the
 * INFINITE multiple-scattering series by assuming the multiply-scattered
 * radiance is locally isotropic and uniform (so the phase functions integrate
 * to 1 and a geometric series closes the higher orders).
 *
 * Parameterization (32×32), Hillaire §5.4:
 *   uv.x ← (cosSunZenith + 1) / 2      (sun zenith cosine, full −1..1 range)
 *   uv.y ← (r − Rg) / (Rt − Rg)        (normalised altitude)
 *
 * Per texel we evaluate, over `dirCount` uniformly distributed sphere
 * directions (Hillaire uses 8×8 = 64 in UE; the isotropic assumption makes
 * even 8–16 directions converge well), two quantities accumulated along each
 * direction's ray-march:
 *
 *   L_2nd      — 2nd-order in-scattered radiance assuming unit incident light
 *   f_ms       — fraction of light that gets scattered back into the medium
 *                (the "multiple-scattering transfer" integral)
 *
 * The closed-form infinite series (eq. 10 in the paper):
 *
 *   F_ms      = 1 / (1 − f_ms)                       (geometric sum)
 *   Ψ_ms      = L_2nd · F_ms
 *
 * Ψ_ms is stored in RGB. It is added (× sun illuminance × per-channel
 * scattering) as an isotropic ambient term inside the sky-view march.
 *
 * Reference:
 *   Hillaire 2020, §5 + eqs. (5)–(10). UE SkyAtmosphere MultiScattLutCS.
 */

import type * as THREE from 'three/webgpu';
import type { AtmosphereConfig, Vec3 } from '../AtmosphereConfig.js';
import { computeDensities } from '../AtmosphereConfig.js';
import { rayAtmosphereIntersect, transmittanceUV } from '../lutParameterization.js';
import {
  makeFloatTexture, sampleTransmittanceLut, clamp01, rayNearIntersect, earthShadowCpu,
} from './TransmittanceLut.js';

/** Canonical Hillaire multi-scatter LUT size. */
export const MULTISCATTER_LUT_SIZE = 32;

/** Ray-march steps per direction inside the multi-scatter integral. */
export const MULTISCATTER_MARCH_STEPS = 20;

/**
 * Uniformly distributed sphere directions (Hillaire's "isotropic" sampling) — 64, his reference's 8×8 (2026-10-06, THE
 * LOCAL SUN). Once each sample reads its own sun angle, a point under a set sun is lit only by the air in a narrow cone
 * toward the sun, and 16 directions mostly miss it: the drawn zenith read 2.2–2.7× bright at −10°/−12° (Ψ_ms 0.03× to
 * 7.9× of a 1024-direction reference at single points). 64 keep the zenith within ~0.4 stops everywhere (≤ 3 % down to
 * −8°); 128 are within 2 % at twice the cost. ~230 ms once per atmosphere config (tier A — Atlas bakes it at boot).
 */
export const MULTISCATTER_DIR_COUNT = 64;
/** The uniform phase p_u of the multiple-scattering approximation (Hillaire 2020 §5.1). */
export const INV_4PI = 1 / (4 * Math.PI);

/** Map (cosSunZenith, altitude) → multi-scatter LUT UV. */
export function multiScatterUV(
  cfg: AtmosphereConfig, radius: number, cosSunZenith: number,
): { u: number; v: number } {
  const u = clamp01(cosSunZenith * 0.5 + 0.5);
  const v = clamp01((radius - cfg.bottomRadius) / (cfg.topRadius - cfg.bottomRadius));
  return { u, v };
}

/** Inverse: multi-scatter LUT UV → (radius, cosSunZenith). */
export function multiScatterUVInverse(
  cfg: AtmosphereConfig, u: number, v: number,
): { radius: number; cosSunZenith: number } {
  const cosSunZenith = clamp01(u) * 2.0 - 1.0;
  const radius = cfg.bottomRadius + clamp01(v) * (cfg.topRadius - cfg.bottomRadius);
  return { radius, cosSunZenith };
}

/**
 * Generate `n` (near-)uniformly distributed unit directions on the sphere via
 * the Fibonacci spiral. Deterministic — used identically on the CPU bake.
 */
export function uniformSphereDirections(n: number): Vec3[] {
  const dirs: Vec3[] = [];
  const golden = Math.PI * (3.0 - Math.sqrt(5.0)); // ≈2.39996 rad
  for (let i = 0; i < n; i++) {
    const z = 1.0 - (2.0 * i + 1.0) / n;       // z ∈ (−1, 1), centred
    const r = Math.sqrt(Math.max(0, 1.0 - z * z));
    const phi = i * golden;
    dirs.push({ x: r * Math.cos(phi), y: z, z: r * Math.sin(phi) });
  }
  return dirs;
}

export interface MultiScatterContext {
  cfg: AtmosphereConfig;
  /** Baked transmittance LUT (Float RGBA). */
  transmittanceData: Float32Array;
  transmittanceWidth: number;
  transmittanceHeight: number;
}

/**
 * CPU reference: evaluate Hillaire's Ψ_ms at (radius, cosSunZenith).
 *
 * For each of `dirCount` uniform sphere directions we march `steps`
 * segments, accumulating:
 *   - L2  : 2nd-order in-scattered radiance (uses single-scattering of the
 *           sun reaching each sample, isotropic phase 1/4π folded in).
 *   - fms : the medium's scattering-back fraction.
 * Then F = 1/(1 − fms), Ψ = L2 · F.
 *
 * The sun is in the local +up frame with zenith cosine `cosSunZenith`, at azimuth +X. THE LOCAL SUN (2026-10-06):
 * every sample along a direction's ray reads the sun's zenith cosine AT that sample, (r·μs + t·ν)/r_s with ν the
 * direction's cosine to the sun (Hillaire's reference: `dot(SunDir, P / |P|)`). These rays run to the top of the
 * atmosphere — hundreds of km — so the old flat frame (the camera's μs at every sample) was no short-path
 * approximation: with the sun under the horizon every sample read as shadowed and Ψ_ms fell to ZERO over nearly half
 * the LUT (448 of 1024 texels), where the air toward the sun is still lit.
 */
export function evaluateMultiScatter(
  ctx: MultiScatterContext,
  radius: number,
  cosSunZenith: number,
  dirCount = MULTISCATTER_DIR_COUNT,
  steps = MULTISCATTER_MARCH_STEPS,
): Vec3 {
  const { cfg } = ctx;
  const dirs = uniformSphereDirections(dirCount);
  // Accumulate L2 (RGB) and fms (RGB) averaged over the sphere.
  let l2x = 0, l2y = 0, l2z = 0;
  let fmsx = 0, fmsy = 0, fmsz = 0;
  // Solid-angle weight per direction for the sphere average (∫dΩ = 4π,
  // isotropic so each dir carries 4π/n; the 1/4π isotropic phase cancels it,
  // leaving a simple 1/n average — Hillaire's "uniformPhase" simplification).
  const wPerDir = 1.0 / dirCount;
  // The sun direction in the local frame (azimuth +X): (sin θs, cos θs, 0).
  const sinSun = Math.sqrt(Math.max(0, 1 - cosSunZenith * cosSunZenith));

  for (const dir of dirs) {
    const muDir = dir.y; // cosine of this direction with local up
    const nu = dir.x * sinSun + dir.y * cosSunZenith; // cosine of this direction with the sun
    const tMax = rayAtmosphereIntersect(radius, muDir, cfg.topRadius);
    // Clamp against the ground: a downward ray stops at the NEAREST surface
    // intersection (not the far root, which would pass through the planet).
    const tGround = rayNearIntersect(radius, muDir, cfg.bottomRadius);
    const tEnd = (tGround > 0) ? Math.min(tMax, tGround) : tMax;
    if (tEnd <= 0) continue;

    const dt = tEnd / steps;
    // Throughput (transmittance) along THIS direction's ray.
    let trX = 1, trY = 1, trZ = 1;

    for (let s = 0; s < steps; s++) {
      const t = (s + 0.5) * dt;
      const rAtSample = Math.sqrt(radius * radius + 2.0 * radius * muDir * t + t * t);
      const altitudeKm = rAtSample - cfg.bottomRadius;
      const d = computeDensities(cfg, altitudeKm);

      // Scattering coefficients at the sample (σ_s = Rayleigh + Mie scatter).
      const sigSx = cfg.rayleighScattering.x * d.rayleigh + cfg.mieScattering.x * d.mie;
      const sigSy = cfg.rayleighScattering.y * d.rayleigh + cfg.mieScattering.y * d.mie;
      const sigSz = cfg.rayleighScattering.z * d.rayleigh + cfg.mieScattering.z * d.mie;

      // Total extinction σ_t = Rayleigh scatter + Mie extinction + ozone.
      const extX = cfg.rayleighScattering.x * d.rayleigh
        + cfg.mieExtinction.x * d.mie + cfg.ozoneExtinction.x * d.ozone;
      const extY = cfg.rayleighScattering.y * d.rayleigh
        + cfg.mieExtinction.y * d.mie + cfg.ozoneExtinction.y * d.ozone;
      const extZ = cfg.rayleighScattering.z * d.rayleigh
        + cfg.mieExtinction.z * d.mie + cfg.ozoneExtinction.z * d.ozone;

      // Per-segment transmittance for THIS direction's throughput.
      const segTx = Math.exp(-extX * dt);
      const segTy = Math.exp(-extY * dt);
      const segTz = Math.exp(-extZ * dt);

      // Sun transmittance from this sample to the top of atmosphere via the transmittance LUT — THE LOCAL SUN: the
      // sun's zenith cosine at this sample, never the bake point's.
      const muSl = (radius * cosSunZenith + t * nu) / rAtSample;
      const muS = muSl > 1 ? 1 : muSl < -1 ? -1 : muSl;
      const tUV = transmittanceUV(cfg, rAtSample, muS);
      const sunT0 = sampleTransmittanceLut(
        ctx.transmittanceData, ctx.transmittanceWidth, ctx.transmittanceHeight,
        tUV.u, tUV.v,
      );
      // The planet's shadow (earthShadowCpu's WHY): no sun reaches a sample the Earth hides.
      const shadow = earthShadowCpu(cfg.bottomRadius, rAtSample, muS);
      const sunT = { x: sunT0.x * shadow, y: sunT0.y * shadow, z: sunT0.z * shadow };

      // 2nd-order in-scatter. Hillaire's L_2ndOrder carries the uniform phase TWICE: once in the sphere sum
      // (the 1/4π that cancels the 4π/n solid angle into wPerDir) and once INSIDE the integrand — the scattering
      // event at the sample itself uses p_u = 1/4π in place of the Rayleigh/Mie phase (the reference's
      // `phaseTimesScattering = σ_s · uniformPhase`). Only the outer one was here, so L2 — and Ψ_ms with it — was
      // 4π (12.57×) too strong: a white ~26 kcd/m² horizon (the CPU sampler's 1° ring at 0.262/sr, a real clear
      // sky 8–12) over a 3 kcd/m² zenith, the WebGL2 band, and a "clear" fog tuned dense to hide it (the lighting
      // agent's finding, the coordinator's fix, 2026-09-27). f_ms below keeps ONE 1/4π (the reference's
      // MultiScatAs1 integrand has no phase term).
      //   L2 += throughput · σ_s · p_u · sunT · dt
      l2x += trX * sigSx * sunT.x * dt * wPerDir * INV_4PI;
      l2y += trY * sigSy * sunT.y * dt * wPerDir * INV_4PI;
      l2z += trZ * sigSz * sunT.z * dt * wPerDir * INV_4PI;

      // fms: fraction scattered (without the sun term) — the medium's
      //   "albedo×density" integral that feeds the geometric series.
      fmsx += trX * sigSx * dt * wPerDir;
      fmsy += trY * sigSy * dt * wPerDir;
      fmsz += trZ * sigSz * dt * wPerDir;

      trX *= segTx; trY *= segTy; trZ *= segTz;
    }

    // The ground bounce (Hillaire's `L += globalL · T_sun · throughput · NdotL · groundAlbedo / π` when the ray ends
    // on the planet): sunlight the ground reflects is scattered back down by the air — over a 0.4 ground that is
    // ~half of Ψ_ms at a high sun (the reason a real sky over land is brighter and whiter than the Rayleigh dome).
    // `groundAlbedo` sat in the config (and its hash) unread until 2026-09-27 (the coordinator's review) — a
    // "real but unwired" term. A reflection, not a scattering event: no inner 1/4π; the outer one is wPerDir's.
    if (tGround > 0 && tEnd === tGround) {
      // THE LOCAL SUN at the ground point the ray reached.
      const muGl = (radius * cosSunZenith + tGround * nu) / cfg.bottomRadius;
      const muG = muGl > 1 ? 1 : muGl < -1 ? -1 : muGl;
      const ndotl = Math.max(0, muG);
      if (ndotl > 0) {
        const gUV = transmittanceUV(cfg, cfg.bottomRadius, muG);
        const gT = sampleTransmittanceLut(
          ctx.transmittanceData, ctx.transmittanceWidth, ctx.transmittanceHeight, gUV.u, gUV.v,
        );
        const k = ndotl * wPerDir / Math.PI;
        l2x += trX * gT.x * cfg.groundAlbedo.x * k;
        l2y += trY * gT.y * cfg.groundAlbedo.y * k;
        l2z += trZ * gT.z * cfg.groundAlbedo.z * k;
      }
    }
  }

  // Geometric series closure: F = 1/(1 − fms); guard fms < 1.
  const fx = 1.0 / Math.max(1e-4, 1.0 - Math.min(fmsx, 0.999));
  const fy = 1.0 / Math.max(1e-4, 1.0 - Math.min(fmsy, 0.999));
  const fz = 1.0 / Math.max(1e-4, 1.0 - Math.min(fmsz, 0.999));
  return { x: l2x * fx, y: l2y * fy, z: l2z * fz };
}

export interface MultiScatterLutResult {
  data: Float32Array;
  width: number;
  height: number;
  texture: THREE.DataTexture;
}

/** Bake the multi-scatter LUT (32×32 Float RGBA) from a transmittance LUT. */
export function bakeMultiScatterLut(
  ctx: MultiScatterContext,
  size = MULTISCATTER_LUT_SIZE,
  dirCount = MULTISCATTER_DIR_COUNT,
  steps = MULTISCATTER_MARCH_STEPS,
): MultiScatterLutResult {
  const data = new Float32Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    const v = (y + 0.5) / size;
    for (let x = 0; x < size; x++) {
      const u = (x + 0.5) / size;
      const { radius, cosSunZenith } = multiScatterUVInverse(ctx.cfg, u, v);
      const psi = evaluateMultiScatter(ctx, radius, cosSunZenith, dirCount, steps);
      const i = (y * size + x) * 4;
      data[i + 0] = psi.x;
      data[i + 1] = psi.y;
      data[i + 2] = psi.z;
      data[i + 3] = 1.0;
    }
  }
  const texture = makeFloatTexture(data, size, size, 'SkyMultiScatterLUT');
  return { data, width: size, height: size, texture };
}

/** Bilinear sample of a baked multi-scatter LUT (mirrors GPU sampling). */
export function sampleMultiScatterLut(
  data: Float32Array, size: number, u: number, v: number,
): Vec3 {
  return sampleTransmittanceLut(data, size, size, u, v);
}
